package com.advaita.cloud.drive.shared

/** A folder row in one level of an [ArchiveTree]. [path] is its full segment path, for navigation. */
data class ArchiveFolderNode(val name: String, val path: List<String>)

/** A file row in one level of an [ArchiveTree]. */
data class ArchiveFileNode(
    val name: String,
    val size: Long,
    val encrypted: Boolean,
    /** Shown at the root under its whole name. See [ArchiveEntryDto.unsafePath] and [ArchiveTree.MAX_DEPTH]. */
    val unsafePath: Boolean,
    /** Only for an unsafe entry, which is never turned into a folder: it was a directory. */
    val directory: Boolean = false,
)

/** One level of the tree: folders first, then files, each in case-insensitive name order. */
data class ArchiveFolderView(
    val path: List<String>,
    val folders: List<ArchiveFolderNode>,
    val files: List<ArchiveFileNode>,
)

/**
 * ADV-1228. An archive listing as folders, built once from the flat paths the
 * server sends.
 *
 * A zip does not have to declare its folders: `a/b/c.txt` may arrive with no
 * `a/` or `a/b/` entry. Every folder a path passes through is therefore created
 * here, and an explicit directory entry merges with the implicit one.
 *
 * An [ArchiveEntryDto.unsafePath] entry is never split. Its name is a claim the
 * archive makes about a place outside itself, so it is shown at the root under
 * its whole name rather than building a folder called `..`.
 *
 * A path of more than [MAX_DEPTH] segments is treated the same way: shown whole
 * at the root and flagged unsafe. Each folder is a node holding its children, so
 * the work is linear in the segments the listing holds.
 */
class ArchiveTree private constructor(private val root: Node) {

    private class Node {
        val folders = LinkedHashMap<String, Node>()
        val files = ArrayList<ArchiveFileNode>()
    }

    /** The level at [path], or null when no folder has that path. The root is `emptyList()`. */
    fun folder(path: List<String>): ArchiveFolderView? {
        var node = root
        for (segment in path) node = node.folders[segment] ?: return null
        return ArchiveFolderView(
            path = path,
            folders = node.folders.keys.sortedWith(NAME_ORDER).map { ArchiveFolderNode(it, path + it) },
            files = node.files.sortedWith(compareBy<ArchiveFileNode, String>(NAME_ORDER) { it.name }),
        )
    }

    /**
     * The level at [path], or the root when [path] no longer resolves. The
     * returned [ArchiveFolderView.path] is the one actually shown, so a caller
     * draws its breadcrumbs and its Up button from it.
     */
    fun folderOrRoot(path: List<String>): ArchiveFolderView = folder(path) ?: folder(emptyList())!!

    /**
     * The level to SHOW for [path]: [path] itself (or the root when it no longer
     * resolves), then — when [skipSingleFolders] — down through every level that holds
     * exactly one folder and nothing else. Such a level offers no choice: a zip of
     * `project/` opens straight into `project`.
     *
     * Walks the raw nodes and sorts nothing, so a caller can resolve the path cheaply
     * and build one [folder] view for the level it lands on.
     */
    fun landing(path: List<String>, skipSingleFolders: Boolean = true): List<String> {
        var shown = if (nodeAt(path) != null) path else emptyList()
        var node = nodeAt(shown)!!
        if (skipSingleFolders) {
            while (node.files.isEmpty() && node.folders.size == 1) {
                val (name, child) = node.folders.entries.single()
                shown = shown + name
                node = child
            }
        }
        return shown
    }

    /**
     * Where Up goes from the shown level [path]: the nearest ancestor that [landing]
     * does not skip straight back down from. Null when there is none — every level
     * above is a single-folder level — so the Up button is disabled.
     */
    fun upFrom(path: List<String>, skipSingleFolders: Boolean = true): List<String>? {
        var candidate = path
        while (candidate.isNotEmpty()) {
            candidate = candidate.dropLast(1)
            if (landing(candidate, skipSingleFolders) != path) return candidate
        }
        return null
    }

    /**
     * False for an ancestor of the shown level [shown] that is skipped on the way down:
     * opening it would land straight back on [shown], so its breadcrumb is not a link.
     */
    fun isCrumbTarget(prefix: List<String>, shown: List<String>, skipSingleFolders: Boolean = true): Boolean =
        landing(prefix, skipSingleFolders) != shown

    private fun nodeAt(path: List<String>): Node? {
        var node = root
        for (segment in path) node = node.folders[segment] ?: return null
        return node
    }

    companion object {
        /**
         * The most segments a path may have and still be split into folders.
         * Real archives stay far below it. A deeper path is shown whole and
         * flagged, the way an unsafe path is.
         */
        const val MAX_DEPTH = 256

        private val NAME_ORDER: Comparator<String> = compareBy<String> { it.lowercase() }.thenBy { it }

        fun build(entries: List<ArchiveEntryDto>): ArchiveTree {
            val root = Node()

            // Creates every folder along [path] and returns the deepest one.
            fun ensure(path: List<String>): Node {
                var node = root
                for (segment in path) node = node.folders.getOrPut(segment) { Node() }
                return node
            }

            for (entry in entries) {
                val segments = if (entry.unsafePath) null else entry.path.split('/').filter { it.isNotEmpty() }
                if (segments == null || segments.size > MAX_DEPTH) {
                    root.files += ArchiveFileNode(
                        name = entry.path,
                        size = entry.size,
                        encrypted = entry.encrypted,
                        unsafePath = true,
                        directory = entry.directory,
                    )
                    continue
                }
                if (segments.isEmpty()) continue
                if (entry.directory) {
                    ensure(segments)
                } else {
                    ensure(segments.dropLast(1)).files += ArchiveFileNode(
                        name = segments.last(),
                        size = entry.size,
                        encrypted = entry.encrypted,
                        unsafePath = false,
                    )
                }
            }
            return ArchiveTree(root)
        }
    }
}
